import { ShieldCheck, Wrench } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import WarrantyForm from "@/components/forms/WarrantyForm";
import { Eyebrow, GradientText } from "@/components/Brand";
import { Reveal } from "@/lib/motion";

const TYPES = [
  {
    icon: ShieldCheck,
    title: "Manufacturer Product Warranty",
    text: "The air conditioning equipment itself is generally covered by the applicable manufacturer's warranty and warranty conditions. Warranty periods differ depending on manufacturer and product.",
  },
  {
    icon: Wrench,
    title: "Installation / Workmanship Support",
    text: "Installation-related issues are assessed according to the work performed, installation contract and applicable consumer and trade requirements.",
  },
];

export default function Warranty() {
  return (
    <>
      <SEO
        title="Air Conditioning Warranty & Support | Airconditioning Victoria"
        description="Warranty and after-sales support for air conditioning systems supplied and installed by Airconditioning Victoria. Submit a warranty enquiry online."
        path="/warranty"
      />
      <PageHero
        eyebrow="After-Sales Support"
        titleLines={["Support when", <GradientText key="g">you need it.</GradientText>]}
        intro="A new air conditioning system is a long-term investment. If there is an issue with equipment supplied or installation work completed by Airconditioning Victoria, our team can help identify the appropriate next step."
        ctaText="Submit a Warranty Enquiry"
        ctaTo="#warranty-form"
        image="/assets/warranty-hero.webp"
        imageAlt="Airconditioning Victoria technician servicing a Daikin ducted system in a ceiling cavity"
      />

      {/* TYPES */}
      <section className="bg-offwhite py-24 md:py-32" data-testid="warranty-types-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-2xl">
            <Eyebrow>Coverage</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Two sides to <GradientText>your cover.</GradientText>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {TYPES.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.1}>
                <div className="h-full rounded-[20px] border border-cardline bg-white p-8 shadow-[0_8px_32px_rgba(5,59,117,0.04)] transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-brand-cyan">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-brand">
                    <t.icon className="h-6 w-6 text-white" />
                  </span>
                  <h3 className="mt-5 font-display text-sm font-bold uppercase tracking-[0.15em] text-navy">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-mutedblue">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-3xl text-sm leading-relaxed text-mutedblue">
              Warranty periods and conditions vary between manufacturers and products. If you're
              unsure about your coverage, submit an enquiry below and our team will help.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FORM */}
      <section id="warranty-form" className="bg-white py-24 md:py-32" data-testid="warranty-form-section">
        <div className="mx-auto max-w-3xl px-5">
          <Reveal className="text-center">
            <Eyebrow className="justify-center">Warranty Enquiry</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Submit a warranty claim.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-mutedblue">
              Provide as much detail as you can about your system and the issue. Our team will
              review the information and contact you.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <div className="rounded-[20px] border border-cardline bg-offwhite p-7 shadow-[0_16px_50px_rgba(5,59,117,0.07)] md:p-10">
              <WarrantyForm />
            </div>
          </Reveal>
        </div>
      </section>

      <FinalCTA
        heading="Need help with your system?"
        text="Call Airconditioning Victoria or submit a warranty enquiry and our team will review the details."
      />
    </>
  );
}
